<template>
  <div class="expert-tool">
    <header
      class="expert-tool__header"
      v-sticky
      sticky-z-index="8"
      ref="header"
    >
      <stats-header
        :blood-pressure="bloodPressure"
        :weight="weight"
        :cholesterolLDL="cholesterolLDLInPreferredUnit"
        :cholesterol-l-d-l-unit="cholesterolLDLPreferredUnitLabel"
        :glucose="glucoseInPreferredUnit"
        :glucose-unit="glucosePreferredUnitLabel"
        :knowledge-level-name="knowledgeLevelName"
        :behaviour-goals="behaviourGoals"
        :outcome-goals="outcomeGoals"
        :bmi="BMI"
        :bmi-date="BMIDate"
      >
        <template v-slot:button>
          <expert-tool-close @close="close"></expert-tool-close>
        </template>
      </stats-header>
      <extra-parameters-bar
        :gender="gender"
        :age="age"
        :heartRate="heartRate"
        :distanceWalked="lastSixMinuteWalkingTest"
      ></extra-parameters-bar>
    </header>
    <div class="expert-tool__content standard-layout">
      <div class="d-flex justify-space-between flex-column flex-md-row">
        <h2 class="mb-8 standard-layout__heading">
          {{ $ucfirst($t('expertTool.title')) }}
        </h2>
        <div class="d-flex mb-8">
          <expert-tool-save @saved="close">
            <template #button="{ save }">
              <v-btn @click.stop="save" x-large text class="ml-4" :disabled="hasReadOnlyAccess">
                <eva-icon class="patient-create__icon mr-2" name="save-outline"/>
                {{ $ucfirst($t('dss.savePrescription')) }}
              </v-btn>
            </template>
          </expert-tool-save>
          <expert-tool-close @close="close" appearance="button">
            <template #button="{ close }">
              <v-btn @click="close" x-large text class="ml-4" :disabled="hasReadOnlyAccess">
                <eva-icon class="patient-create__icon mr-2" name="close-outline"/>
                {{ $ucfirst($t('general.close', { catalog: 'app_mobile' })) }}
              </v-btn>
            </template>
          </expert-tool-close>
          <expert-tool-print></expert-tool-print>
        </div>
      </div>
      <expert-tool-button-line></expert-tool-button-line>
      <router-view></router-view>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import StatsHeader from '../../components/Parts/StatsHeader';
import ExtraParametersBar from '../../components/Parts/ExtraParametersBar';
import ExpertToolButtonLine from './ExpertToolButtonLine';
import ExpertToolSave from './ExpertToolSave';
import ExpertToolPrint from './ExpertToolPrint';
import ExpertToolClose from './ExpertToolClose'
import RouterHistory from "../../../router/hooks/router-history";
import gql from 'graphql-tag';

/**
 * Component ExpertToolMain.vue
 *
 * @module ExpertToolMain
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.9] app/src/APP_DASHBOARD/templates/views/ExpertTool/ExpertToolMain.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'ExpertTool',
  components: {
    StatsHeader,
    ExtraParametersBar,
    ExpertToolButtonLine,
    ExpertToolSave,
    ExpertToolPrint,
    ExpertToolClose
  },
  computed: {
    ...mapGetters({
      gender: 'patientSummary/gender',
      age: 'patientSummary/age',
      heartRate: 'patientSummary/heartRate',
      distanceWalked: 'patientSummary/distanceWalked',
      bloodPressure: 'patientSummary/bloodPressure',
      weight: 'patientSummary/weight',
      cholesterolLDLInPreferredUnit: 'patientSummary/cholesterolLDLInPreferredUnit',
      cholesterolLDLPreferredUnitLabel: 'patientSummary/cholesterolLDLPreferredUnitLabel',
      glucoseInPreferredUnit: 'patientSummary/glucoseInPreferredUnit',
      glucosePreferredUnitLabel: 'patientSummary/glucosePreferredUnitLabel',
      knowledgeLevelName: 'patientSummary/knowledgeLevelName',
      behaviourGoals: 'patientSummary/behaviourGoals',
      outcomeGoals: 'patientSummary/outcomeGoals',
      BMI: 'patientSummary/BMI',
      BMIDate: 'patientSummary/BMIDate',
      patientId: 'patientSummary/id',
      hasReadOnlyAccess: 'user/hasReadOnlyAccess'
    }),
  },
  data() {
    return {
      lastSixMinuteWalkingTest: null
    }
  },
  methods:{
    close() {
      const expertToolRoute = this.$router.resolve({ name: 'PatientExpertTool' });
      this.$store.dispatch('patientSummary/toggleUnsavedChanges', false);
      RouterHistory.back(
          { before: expertToolRoute.href },
          { name: 'PatientRecordPAGoalSettingWeeklySportsGoal' }
      );
    },
    get6MWTData() {
      this.$app.apolloMobileClient
        .query({
          query: gql`
            query mySixMinuteWalkingTests(
              $patientId: String!
            ) {
              mySixMinuteWalkingTests(
                functions: [{ function: patientId, parameters: [$patientId] }]
              ) {
                id
                distanceWalked
                createdAt
                version
              }
            }
          `,
          variables: {
            patientId: this.patientId,
          },
        })
        .then((result) => {
          const data = result.data?.mySixMinuteWalkingTests;
          if (data && data.length) {
            // get distanceWalked of last 6MWT
            this.lastSixMinuteWalkingTest = data[data.length-1].distanceWalked
          }
        })
    }
  },
  mounted() {
    // distanceWalked = last COMPLETED Walking Test
    // if it's null there is no need to make a request to get 6MWT data - it will be null as well
    if (this.distanceWalked) {
      this.get6MWTData()
    }
  }
};
</script>

<style lang="scss">
.expert-tool__header {
  background-color: #fff;
}

.expert-tool__icon {
  height: 24px;
}

.expert-tool__table {
  font-family: Roboto, sans-serif;
  font-weight: 400;

  table > thead > tr > th,
  table > tbody > tr > td {
    font-size: 1rem !important;
    line-height: 1.6 !important;
  }
}

.expert-tool__table-cell-content {
  display: flex;
  align-items: center;
}

.expert-tool__table-cell-icon {
  margin-right: 10px;
}

.expert-tool__table-item-name {
  font-weight: 600;
}

.expert-tool__frequency-label {
  white-space: nowrap;
}

.expert-tool__table-checkbox-cell {
  width: 50px;
}
</style>
